// Within-day pace logic, ported from the Claude Design handoff `data/horses.js`.
// Source of truth for what status each horse should show on the dashboard
// given how much they've drunk vs how much they should have drunk by now.
// The total daily target (targetL) comes from src/utils/eheCalculator.ts.

export type PaceStatus = "hydrated" | "mild" | "moderate" | "severe" | "critical";
export type CardState = "ok" | "behind" | "severe" | "stale";

export interface Freshness {
  kind: "fresh" | "stale";
  hours: number | null;
}

export interface HorseFlags {
  droppings?: boolean;
  behaviour?: boolean;
  combined?: boolean;
}

export interface PaceResult {
  status: PaceStatus;
  expectedL: number;
  ratio: number;
  paceFrac: number;
  wasCapped: boolean;
  rawStatus: PaceStatus;
}

export interface PaceLabel {
  tone: "neutral" | "ahead" | "onpace" | "slight" | "moderate" | "severe";
  text: string;
}

// Typical drinking-day shape — fraction of daily intake per window.
// [startHour, endHour, fractionOfDailyIntake]
export const DRINK_DAY: ReadonlyArray<readonly [number, number, number]> = [
  [6, 10, 0.25],
  [10, 12, 0.15],
  [12, 15, 0.15],
  [15, 18, 0.20],
  [18, 20, 0.15],
  [20, 22, 0.10],
];

// Sort order so concerning horses appear first.
export const STATUS_ORDER: Record<PaceStatus | "stale", number> = {
  critical: 0,
  severe: 1,
  moderate: 2,
  stale: 3,
  mild: 4,
  hydrated: 5,
};

const STATUS_RANK: Record<PaceStatus, number> = {
  hydrated: 0,
  mild: 1,
  moderate: 2,
  severe: 3,
  critical: 4,
};

const RANK_TO_STATUS: PaceStatus[] = ["hydrated", "mild", "moderate", "severe", "critical"];

export function paceFraction(hour: number): number {
  let cum = 0;
  for (const [start, end, frac] of DRINK_DAY) {
    if (hour >= end) cum += frac;
    else if (hour > start) cum += frac * (hour - start) / (end - start);
  }
  return Math.min(1, cum);
}

export function rawPaceStatus(ratio: number): PaceStatus {
  if (ratio >= 0.85) return "hydrated";
  if (ratio >= 0.60) return "mild";
  if (ratio >= 0.30) return "moderate";
  if (ratio >= 0.10) return "severe";
  return "critical";
}

// Early in the day, even very low ratios stay quiet (can't be "critical" at 7am).
export function timeCap(hour: number): PaceStatus {
  if (hour < 10) return "mild";
  if (hour < 15) return "moderate";
  if (hour < 19) return "severe";
  return "critical";
}

export function capStatus(raw: PaceStatus, cap: PaceStatus): PaceStatus {
  const r = STATUS_RANK[raw];
  const c = STATUS_RANK[cap];
  return RANK_TO_STATUS[Math.min(r, c)]!;
}

export function computePace(drunkL: number, targetL: number, hour: number): PaceResult {
  const paceFrac = paceFraction(hour);
  const expectedL = targetL * paceFrac;
  if (expectedL <= 0.5) {
    return { status: "hydrated", expectedL: 0, ratio: 1, paceFrac, wasCapped: false, rawStatus: "hydrated" };
  }
  const ratio = drunkL / expectedL;
  let raw = rawPaceStatus(ratio);
  // Zero-intake floor: drunk ~nothing when meaningful intake expected.
  if (drunkL < 2 && expectedL > 6 && raw !== "critical") raw = "severe";
  const cap = timeCap(hour);
  const capped = capStatus(raw, cap);
  return { status: capped, expectedL, ratio, paceFrac, wasCapped: capped !== raw, rawStatus: raw };
}

export function paceLabel(drunkL: number, expectedL: number): PaceLabel {
  if (expectedL <= 0.5) return { tone: "neutral", text: "Day not started" };
  const diff = drunkL - expectedL;
  const ratio = drunkL / expectedL;
  const absDiff = Math.abs(diff);
  if (ratio >= 1.10) return { tone: "ahead",    text: `Ahead +${Math.round(diff)}L` };
  if (ratio >= 0.85) return { tone: "onpace",   text: "On pace" };
  if (ratio >= 0.60) return { tone: "slight",   text: `Slightly behind −${Math.round(absDiff)}L` };
  if (ratio >= 0.30) return { tone: "moderate", text: `Moderately behind −${Math.round(absDiff)}L` };
  if (drunkL < 2 && expectedL > 6) return { tone: "severe", text: "Drunk almost nothing" };
  return { tone: "severe", text: `Significantly behind −${Math.round(absDiff)}L` };
}

export function computeFreshness(lastLoggedMinAgo: number | null | undefined): Freshness {
  if (lastLoggedMinAgo == null) return { kind: "stale", hours: null };
  const h = lastLoggedMinAgo / 60;
  if (h < 24) return { kind: "fresh", hours: h };
  return { kind: "stale", hours: h };
}

// 5-tier pace status + freshness + flags → 3-colour card state (+ stale).
export function toCardState(
  paceStatus: PaceStatus,
  freshness: Freshness,
  flags?: HorseFlags
): CardState {
  if (freshness.kind === "stale") return "stale";
  if (paceStatus === "severe" || paceStatus === "critical" || flags?.combined) return "severe";
  const hasFlag = !!(flags?.droppings || flags?.behaviour);
  if (paceStatus === "mild" || paceStatus === "moderate" || hasFlag) return "behind";
  return "ok";
}

export function fmtAgo(mins: number | null | undefined): string {
  if (mins == null) return "Never";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const STATUS_LABEL_MAP: Record<PaceStatus | "stale" | "ahead", string> = {
  hydrated: "Hydrated",
  mild: "Mild",
  moderate: "Moderate",
  severe: "Severe",
  critical: "Critical",
  stale: "Stale",
  ahead: "Hydrated",
};

export function statusLabel(s: PaceStatus | "stale" | "ahead"): string {
  return STATUS_LABEL_MAP[s];
}

export function trendDirection(history5: (PaceStatus | null)[] | null | undefined):
  "declining" | "improving" | "stable" {
  if (!history5) return "stable";
  const known = history5.filter((s): s is PaceStatus => s != null);
  if (known.length < 3) return "stable";
  const points = known.map((s, i) => ({ i, v: STATUS_RANK[s] }));
  const n = points.length;
  const meanI = (n - 1) / 2;
  const meanV = points.reduce((a, p) => a + p.v, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of points) {
    num += (p.i - meanI) * (p.v - meanV);
    den += (p.i - meanI) ** 2;
  }
  const slope = den ? num / den : 0;
  if (slope > 0.35) return "declining";
  if (slope < -0.35) return "improving";
  return "stable";
}

export function projectEndOfDay(drunkL: number, hour: number): number {
  const frac = paceFraction(hour);
  if (frac <= 0.01) return drunkL;
  return drunkL / frac;
}

// Current hour as a fractional float (e.g. 10.7 for 10:42).
export function currentHour(date: Date = new Date()): number {
  return date.getHours() + date.getMinutes() / 60;
}
